import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DataGrid, { Column, MasterDetail, Editing, RequiredRule, Selection } from 'devextreme-react/data-grid';
import DropDownBox from 'devextreme-react/drop-down-box';
import { TextBox } from 'devextreme-react';
import { RowInsertingEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';

import { formatMessage } from 'devextreme/localization';

import AIGroupPathTemplate from './AIGroupPathTemplate';

import AIAssistantChatPopup, { AIAssistantChatPopupHandle } from '../../components/ai-assistant-chat-popup/AIAssistantChatPopup';

import AIGroupApi from '../../api/AIGroupApi';
import UserApi from '../../api/UserApi';

import AIGroupDto from '../../classes/dtos/assistantmodule/AIGroupDto';
import UserListDto from '../../classes/dtos/accountmodule/UserListDto';

import './ai-groups.scss';

function GridInDropDownCell({ cellInfo, users }: { cellInfo: any; users: UserListDto[] | null }) {
    const [selectedKeys, setSelectedKeys] = useState(cellInfo.value || []);
    const [isOpened, setIsOpened] = useState(false);

    const onSelectionChanged = (e: any) => {
        setSelectedKeys(e.selectedRowKeys);
        cellInfo.setValue(e.selectedRowKeys);
    };

    return (

        <DropDownBox
            value={selectedKeys}
            opened={isOpened}
            valueExpr="Id"
            placeholder={formatMessage("AIGroupsGrid_SelectAUser")}
            showClearButton={false}
            dataSource={users}
            onValueChanged={(e) => {
                setSelectedKeys(e.value || []);
                cellInfo.setValue(e.value || []);
            }}
            onOpenedChange={(opened) => setIsOpened(opened)}
            fieldRender={() => (
                <TextBox>
                    <div style={{ marginLeft: '16px', flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px', cursor: 'pointer' }}>
                        {(users || [])
                            .filter(u => (cellInfo.value ?? []).includes(u.Id))
                            .map((u, idx, arr) => (
                                <span key={u.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                                    {u.Label}
                                </span>
                            ))
                        }
                        {(!cellInfo.value || cellInfo.value.length === 0) && (
                            <span>
                                {formatMessage("AIGroupsGrid_NoUsers")}
                            </span>
                        )}
                    </div>
                </TextBox>
            )}
            contentRender={() => (
                <DataGrid
                    dataSource={users}
                    selectedRowKeys={selectedKeys}
                    onSelectionChanged={onSelectionChanged}
                    hoverStateEnabled={true}
                    height={300}
                    keyExpr="Id"
                    showBorders={true}
                >
                    <Selection mode="multiple" showCheckBoxesMode="always" />
                    <Column dataField="FirstName" caption="FirstName" minWidth={200} />
                    <Column dataField="LastName" caption="LastName" minWidth={200} />
                    <Column dataField="Email" caption="E-mail" minWidth={200} />
                </DataGrid>
            )}
        />
    );
}

export default function AIGroups() {

    const [aiGroupDtos, setAIGroupDtos] = useState<AIGroupDto[] | null>(null);
    const [users, setUsers] = useState<UserListDto[] | null>(null);

    const aiAssistantChatPopupHandleRef = useRef<AIAssistantChatPopupHandle>(null);

    useEffect(() => {
        if (aiGroupDtos === null) {
            AIGroupApi.GetAllAIGroupSettingsAsync()
                .then(detail => {
                    setAIGroupDtos(detail.Result);
                });
        }
    }, [aiGroupDtos]);

    useEffect(() => {
        if (users === null) {
            UserApi.GetUserListAsync(false)
                .then(list => {
                    setUsers(list.Result);
                });
        }
    }, [users]);

    const insertRow = useCallback((e: RowInsertingEvent<AIGroupDto, any>) => {

        if (aiGroupDtos === null) {
            e.cancel = true;
            return;
        }

        AIGroupApi.CreateAIGroupAsync(e.data.Name, e.data.UserIds ?? [])
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setAIGroupDtos(null);
            });
    }, [aiGroupDtos]);

    const updateRow = useCallback((e: RowUpdatingEvent<AIGroupDto, any>) => {

        if (aiGroupDtos === null) {
            e.cancel = true;
            return;
        }

        let aiGroupId = e.newData.AIGroupId ?? e.oldData.AIGroupId;
        let name = e.newData.Name ?? e.oldData.Name;
        let userIds = e.newData.UserIds ?? e.oldData.UserIds;

        AIGroupApi.UpdateAIGroupAsync(aiGroupId, name, userIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setAIGroupDtos(null);
            });
    }, [aiGroupDtos]);

    const removeRow = useCallback((e: RowRemovingEvent<AIGroupDto, any>) => {

        if (aiGroupDtos === null) {
            e.cancel = true;
            return;
        }

        AIGroupApi.DeleteAIGroupAsync(e.data.AIGroupId)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setAIGroupDtos(null);
            });
    }, [aiGroupDtos]);

    const refresh = useCallback(() => {
        setAIGroupDtos(null);
    }, []);

    const handleClick = useCallback((aiGroupDto: AIGroupDto) => {
        if (aiAssistantChatPopupHandleRef.current !== null) {
            aiAssistantChatPopupHandleRef.current.Open(aiGroupDto.AIGroupId);
        }
    }, []);

    const renderLinkCell = useCallback((cellData: any) => {
        return (
            <a onClick={(e) => {
                e.preventDefault();
                handleClick(cellData.data);
            }}>
                Open assistant
            </a>
        );
    }, [handleClick]);

    // Composant DropDownBox avec DataGrid interne
    const GridInDropDown = useCallback((cellInfo: any) => (
        <GridInDropDownCell cellInfo={cellInfo} users={users} />
    ), [users]);

    const userTagsCell = useCallback((cellData: any) => {
        const userIds = cellData.value || [];

        const selectedItems = (users ?? []).filter(u =>
            userIds.includes(u.Id)
        );

        return (
            <div style={{ flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px' }}>
                {selectedItems.length === 0 ? (
                    <span>{formatMessage("AIGroupsGrid_NoUsers")}</span>
                ) : (
                    selectedItems.map((u, idx, arr) => (
                        <span key={u.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                            {u.Label}
                        </span>
                    ))
                )}
            </div>
        );
    }, [users]);

    // users peut encore etre null quand les groupes arrivent en premier :
    // lever une exception ici casserait le rendu interne de la grille.
    const calculateUsersDisplayValue = useCallback((rowData: AIGroupDto) => {
        if (!rowData.UserIds || rowData.UserIds.length === 0) {
            return formatMessage("AIGroupsGrid_NoUsers");
        }
        return rowData.UserIds
            .map((id: number) => {
                const user = (users ?? []).find(u => u.Id === id);
                return user ? user.Label : '';
            })
            .filter(Boolean)
            .join(', ');
    }, [users]);

    const renderMasterDetail = useCallback((detailData: any) => (
        <AIGroupPathTemplate
            key={detailData.key}
            aiGroupId={detailData.key}
            paths={detailData.data.Paths}
            refresh={refresh}
        />
    ), [refresh]);

    const editingTexts = useMemo(() => ({ addRow: 'Add a new AI group' }), []);

    return (
        <div className="ai-groups-container">
            <div className={"ai-groups-internal-container"}>
                <div className={"ai-groups-title-container"}>
                    <h2>{"All your AI groups"}</h2>
                </div>
                <div>
                    <DataGrid
                        id="grid-container"
                        dataSource={aiGroupDtos}
                        keyExpr="AIGroupId"
                        showBorders={true}
                        onRowInserting={insertRow}
                        onRowUpdating={updateRow}
                        onRowRemoving={removeRow}>

                        <Editing
                            mode="row"
                            allowUpdating={true}
                            allowDeleting={true}
                            allowAdding={true}
                            texts={editingTexts} />

                        <Column dataField="Name" width={270} caption="Name">
                            <RequiredRule message="This field is required." />
                        </Column>

                        <Column
                            dataField="UserIds"
                            caption={formatMessage("AIGroupsGrid_Users")}
                            editCellRender={GridInDropDown}
                            cellRender={userTagsCell}
                            allowSorting={false}
                            calculateDisplayValue={calculateUsersDisplayValue}
                        />

                        <Column caption="" allowSorting={false} allowEditing={false} cellRender={renderLinkCell} />

                        <MasterDetail
                            enabled={true}
                            render={renderMasterDetail}
                        />
                    </DataGrid>
                    <AIAssistantChatPopup ref={aiAssistantChatPopupHandleRef} />
                </div>
            </div>
        </div>
    );
}
